import _cloneDeep from 'lodash/cloneDeep'
import _orderBy from 'lodash/orderBy'
import _sortBy from 'lodash/sortBy'
import {
  createConsolidatedReport,
  deleteConsolidatedReport,
  deleteDiagnosis,
  getActiveDiagnosisTemplate,
  getConsolidatedReport,
  getConsolidatedReportDetail,
  getConsolidatedReportFactory,
  getDiagnoses,
  getDiagnosis,
  getDiagnosisBricksGraph,
  getDiagnosisExpanded,
  getDiagnosisFunctions,
  getDiagnosisFunctionsGraph,
  getDiagnosisNoAppliedPrioritiesAndFunctionsTable,
  getDiagnosisPrioritiesGraph,
  getDiagnosisRadarGraph,
  getDiagnosisRequirements,
  getDiagnosisTemplate,
  getFundamentalsTargetsFactory,
  getFundamentalTargetByMonth,
  getSelfAssessmentsInDiagnosis,
  patchConsolidatedReportJob,
  patchDiagnosis,
  patchFundamentalInConsolidatedReport,
  patchFundamentalInSelfAssessment,
  patchFundamentalTarget,
  saveConsolidatedReportFactory,
  saveDiagnosis,
  saveFundamentalsTargetsFactory,
} from 'frontend/communications/diagnosis'
import { getCurrentTargetStats, getFunctionsStats, getNumberOfZerosStats } from 'frontend/communications/stats'
import { GOOD_PRACTICE } from 'frontcore/constants'
import { MAJOR_DISCREPANCY_TYPE } from 'frontend/constants/diagnosis'

const stateObj = {
  consolidatedReportDetail: null,
  currentDataModified: false,
  diagnoses: [],
  diagnosis: null,
  filters: {},
  diagnosisTemplate: null,
  activeDiagnosisTemplate: null,
  templateRequirements: null,
  activeTemplateRequirements: null,
  reportData: null,
  selfAssessmentMajorDiscrepancies: null,
  fundamentalsInSelfAssessment: [],
}

const gettersObj = {
  getDiagnoses (state) {
    return state.diagnoses
  },
  getDiagnosisById: (state) => (id) => {
    return state.diagnoses.find(diagnosis => diagnosis.id === id)
  },
  getDiagnosis (state) {
    return state.diagnosis
  },
  getFilters (state) {
    return state.filters
  },
  getDiagnosisTemplate: (state) => {
    return state.diagnosisTemplate
  },
  getActiveDiagnosisTemplate: (state) => {
    return state.activeDiagnosisTemplate
  },
  getDiagnosisFunctions: (state) => {
    return state.diagnosisTemplate ? state.diagnosisTemplate.diagnosisFunctions : null
  },
  getReportData: (state) => {
    return state.reportData
  },
  getFunctionsGraph: (state) => {
    return state.reportData.functionsGraph
  },
  getBricksGraph: (state) => {
    return state.reportData.bricksGraph
  },
  getRadarGraph: (state) => {
    return state.reportData.radarGraph
  },
  getPrioritiesGraph: (state) => {
    return state.reportData.prioritiesGraph
  },
  getNoAppliedPrioritiesAndFunctionsTable: (state) => {
    return state.reportData.noAppliedPrioritiesAndFunctionsTable
  },
  getDiagnosisFunctionById: (state) => (functionId) => {
    return state.diagnosisTemplate.diagnosisFunctions ?
      state.diagnosisTemplate.diagnosisFunctions.find(dFunction => dFunction.id === functionId) : {}
  },
  getBricks: (state) => {
    return state.diagnosisTemplate.bricks
  },
  getPrinciples: (state) => {
    return state.diagnosisTemplate.principles
  },
  getPrincipleById: (state) => (principleID) => {
    return state.diagnosisTemplate.principles.find(principle => principle.id === principleID)
  },
  getFundamentalsByBrick: (state) => (brickId) => {
    const fundamentals = state.diagnosisTemplate.bricks.find(brick => brick.id === brickId).fundamentals
    const orderedFundamentals = _cloneDeep(fundamentals).sort((a, b) => {
      if (a.order == b.order && a.traditional_fundamental == null) return -1
      else if (a.order == b.order && a.traditional_fundamental !== null) return 1
      return a.order - b.order
    })
    const diagnosisType = state.diagnosis !== null ? state.diagnosis.type !== undefined && state.diagnosis.type !== null : false
    return diagnosisType ? orderedFundamentals.filter((fundamental) => { return fundamental.type === state.diagnosis.type }) : orderedFundamentals
  },
  getApplicableFundamentalsInDiagnosisByBrick: (state, getters) => (brickId) => {
    const fundamentals = getters.getFundamentalsByBrick(brickId)
    const applicableFundamentalsInDiagnosis = []
    fundamentals.forEach(fundamental => {
      const matchFundamental = state.diagnosis.fundamentals_in_diagnosis.find(
        fundamentalInDiagnosis => fundamentalInDiagnosis.fundamental === fundamental.id &&
          !fundamentalInDiagnosis.person_not_applicable &&
          !(fundamentalInDiagnosis.fully_assessed && fundamentalInDiagnosis.score === null))
      if(matchFundamental) applicableFundamentalsInDiagnosis.push(matchFundamental)
    })
    return applicableFundamentalsInDiagnosis
  },
  getFundamentalById: (state) => (fundamentalId) => {
    return state.diagnosisTemplate.bricks
      .map(brick => brick.fundamentals.find((f => f.id === fundamentalId)))
      .filter(item => item)
      .at(0)
  },
  getFundamentalInDiagnosisById: (state) => (fundamentalId) => {
    return state.diagnosis.fundamentals_in_diagnosis.find((fundamentalInDiagnosis => fundamentalInDiagnosis.fundamental === fundamentalId))
  },
  getDigitalFundamentalInDiagnosis: (state, getters) => (brickID, fundamentalId) => {
    const fundamentals = getters.getFundamentalsByBrick(brickID)
    const digitalFundamental = fundamentals.find(fund => fund.traditional_fundamental == fundamentalId)
    if (!digitalFundamental) return null
    const fundamentalInDiagnosis = state.diagnosis.fundamentals_in_diagnosis.find((item => item.fundamental === digitalFundamental.id))
    return fundamentalInDiagnosis
  },
  getNumberOfMajorDiscrepancies: (state) => {
    return state.diagnosis.major_discrepancies
  },
  getMajorDiscrepanciesByBrickId: (state, getters) => (brickId) => {
    const fundamentals = getters.getFundamentalsByBrick(brickId)
    if(!fundamentals) return []
    const majorDiscrepancies = []
    fundamentals.forEach(fundamental => {
      const fundamentalInDiagnosis = getters.getFundamentalInDiagnosisById(fundamental.id)
      const discrepancy = {
        fundamentalId: fundamental.id,
        reqIds: [],
      }
      if (fundamentalInDiagnosis) {
        fundamentalInDiagnosis.not_applied_requirement_assessments.forEach(notAppliedReq => {
          discrepancy.reqIds.push(notAppliedReq.requirement)
        })
        const major_remark = fundamentalInDiagnosis.remarks.find(remark => remark.field_type === MAJOR_DISCREPANCY_TYPE)
        if(major_remark?.accountable) {
          discrepancy.remark = major_remark
        }
        if(discrepancy.reqIds.length || discrepancy.remark) majorDiscrepancies.push(discrepancy)
      }

    })
    return majorDiscrepancies
  },
  getRequirementById: (state) => (requirementId) => {
    return state.templateRequirements ? state.templateRequirements.find(requirement => requirement.id === requirementId) : null
  },
  getActiveTemplateRequirements: (state) => {
    return state.activeTemplateRequirements
  },
  isLoadingRequirements: (state) => {
    return state.templateRequirements == null
  },
  isLoadingActiveRequirements: (state) => {
    return state.activeTemplateRequirements == null
  },
  getFullyAssessedNumberOfFundamentalsForBrick: (state, getters) => (brickId) => {
    const fundamentals = getters.getFundamentalsByBrick(brickId)
    let count = 0
    fundamentals.forEach( fundamental => {
      const assessment = state.diagnosis.fundamentals_in_diagnosis
        .find(fundamentalInDiagnosis => fundamentalInDiagnosis.fundamental === fundamental.id)
      if(assessment?.fully_assessed && assessment?.score !== null) count++
    })
    return count
  },
  getNotAssessedNumberOfFundamentalsForBrick: (state, getters) => (brickId) => {
    const fundamentals = getters.getFundamentalsByBrick(brickId)
    let count = 0
    fundamentals.forEach( fundamental => {
      const assessment = state.diagnosis.fundamentals_in_diagnosis
        .find(fundamentalInDiagnosis => fundamentalInDiagnosis.fundamental === fundamental.id)
      if(assessment && (assessment.person_not_applicable != null || (assessment.fully_assessed && assessment.score == null))) count++
    })
    return count
  },
  isFullyAssessedBrick: (state, getters) => (brickId) => {
    return (getters.getFullyAssessedNumberOfFundamentalsForBrick(brickId) + getters.getNotAssessedNumberOfFundamentalsForBrick(brickId)) === getters.getFundamentalsByBrick(brickId).length
  },
  isLoadingReportData: (state) => {
    return state.reportData == null || state.reportData.diagnosis !== state.diagnosis.id
  },
  getBrickScore: (state, getters) => (brickId) => {
    const brickScoreData = getters.getBricksGraph.find(brick => brick.id == brickId)
    return brickScoreData ? brickScoreData.score : ''
  },
  getBrickDigitalScore: (_, getters) => (brickId) => {
    const brickData = getters.getBricksGraph.find(brick => brick.id == brickId)
    return brickData ? brickData.digital_score : ''
  },
  getSelfAssessmentScore: (state, getters) => {
    const selfAssessedData = getters.getRadarGraph
    return selfAssessedData ? selfAssessedData[0].self_assessments_total_score : 0
  },
  getTargetScore: (state, getters) => {
    const targetData = getters.getRadarGraph
    return targetData ? targetData[0].target_total_score : 0
  },
  exceedLimitOfMajorDiscrepancies: (state, getters) => {
    return getters.getNumberOfMajorDiscrepancies > getters.getDiagnosisTemplate.major_discrepancies_lower_limit
  },
  someMajorDiscrepancies: (state, getters) => {
    return getters.getNumberOfMajorDiscrepancies > 0
  },
  getSelfAssessmentMajorDiscrepancies: (state) => {
    return state.selfAssessmentMajorDiscrepancies
  },
  getAllAssessmentsWithEvidences: (state, getters) => {
    const allAssessmentsWithEvidences = []
    state.diagnosis.fundamentals_in_diagnosis.forEach(fundamentalInDiagnosis => {
      const fullyAppliedAssessments = _filterRequirementAssessmentsWithEvidences(
        fundamentalInDiagnosis.fully_applied_requirement_assessments,
        fundamentalInDiagnosis,
        getters )
      const partiallyAppliedAssessments = _filterRequirementAssessmentsWithEvidences(
        fundamentalInDiagnosis.partially_applied_requirement_assessments,
        fundamentalInDiagnosis,
        getters)
      const notAppliedAssessments = _filterRequirementAssessmentsWithEvidences(
        fundamentalInDiagnosis.not_applied_requirement_assessments,
        fundamentalInDiagnosis,
        getters)
      allAssessmentsWithEvidences.push(
        fullyAppliedAssessments,
        partiallyAppliedAssessments,
        notAppliedAssessments)
    })
    return [].concat(...allAssessmentsWithEvidences)
  },
  getGoodPractices: (state, getters) => {
    const allAssessments = getters.getAllAssessmentsWithEvidences
    const allEvidences = []
    allAssessments.forEach(assessment => {
      assessment.evidences.forEach(evidence => {
        if(evidence.evidence_type === GOOD_PRACTICE) {
          evidence.title = assessment.fundamentalName
          allEvidences.push(evidence)
        }
      })
    })
    return allEvidences.length > 0 ? allEvidences : null
  },
  getFundamentalInSelfAssessmentByBrick: (state, getters) => (brickId) => {
    const fundamentalsInTemplate = getters.getFundamentalsByBrick(brickId)
    if (state.fundamentalsInSelfAssessment.length) {
      return fundamentalsInTemplate.filter(fundamental => {
        return state.fundamentalsInSelfAssessment.find(f => f.fundamental_in_template === fundamental.id)
      })
    }
    return []
  },
  getFundamentalsInSelfAssessmentByFundamentalInTemplate: (state) => (fundamentalInTemplate) => {
    return state.fundamentalsInSelfAssessment.find(f => f.fundamental_in_template === fundamentalInTemplate)
  },
  getCurrentDataModified (state) {
    return state.currentDataModified
  },
  getConsolidatedReportDetail (state) {
    return state.consolidatedReportDetail
  },
}

const mutations = {
  deleteDiagnosis (state, diagnosisId) {
    state.diagnoses = state.diagnoses.slice().filter(diagnosis => diagnosis.id !== diagnosisId)
  },
  setDiagnosis (state, diagnosis) {
    if (state.diagnoses.find(diagnosisData => diagnosisData.id === diagnosis.id)) {
      const diagnoses = state.diagnoses.slice().filter(d => d.id !== diagnosis.id)
      diagnoses.push(diagnosis)
      state.diagnoses = _orderBy(diagnoses, 'id')
    }
    state.diagnosis = diagnosis
  },
  setDiagnoses (state, diagnoses) {
    state.diagnoses = _orderBy(diagnoses, 'id')
  },
  setFilter (state, filter) {
    state.filters = {
      ...state.filters,
      ...filter,
    }
  },
  setFilters (state, filters) {
    state.filters = filters
  },
  setDiagnosisTemplate (state, template) {
    state.diagnosisTemplate = template
  },
  setActiveDiagnosisTemplate (state, template) {
    state.activeDiagnosisTemplate = template
  },
  setActiveTemplateRequirements (state, requirements) {
    state.activeTemplateRequirements = requirements
  },
  setTemplateRequirements (state, requirements) {
    state.templateRequirements = requirements
  },
  setReportData (state, report) {
    state.reportData = report
  },
  setSelfAssessmentMajorDiscrepancies (state, selfAssessmentMajorDiscrepancies) {
    state.selfAssessmentMajorDiscrepancies = selfAssessmentMajorDiscrepancies
  },
  setFundamentalsInSelfAssessment (state, fundamentalInSelfAssessment) {
    state.fundamentalsInSelfAssessment = fundamentalInSelfAssessment
  },
  setFundamentalInSelfAssessment (state, fundamental) {
    const fundamentalInSelfAssessment = state.fundamentalsInSelfAssessment.find(f => f.id === fundamental.id)
    fundamentalInSelfAssessment.score = fundamental.score
    fundamentalInSelfAssessment.person_not_applicable = fundamental.person_not_applicable
  },
  setCurrentDataModified (state, value) {
    state.currentDataModified = value
  },
  setConsolidatedReportDetail (state, value) {
    state.consolidatedReportDetail = value
  },
  setFundamentalsInConsolidatedReport (state, value) {
    const fundamental = state.consolidatedReportDetail.result.fundamentals_in_diagnosis.find(item => item.id === value.id)
    fundamental.score = value.score
  },
}

const actions = {
  async deleteDiagnosis ({ commit }, id) {
    await deleteDiagnosis(id)
    commit('deleteDiagnosis', id)
  },
  async fetchDiagnoses ({ commit }, { auditors,
    perimeter,
    open,
    start_date_gte,
    start_date_lte,
    close_date_gte,
    close_date_lte } = {}) {
    const diagnoses = await getDiagnoses({
      auditors,
      perimeter,
      open,
      start_date_gte,
      start_date_lte,
      close_date_gte,
      close_date_lte,
    })
    commit('setDiagnoses', diagnoses)
    return diagnoses
  },
  async fetchDiagnosis ({ commit, getters }, id) {
    const storedDiagnosis = getters.getDiagnosis
    if (storedDiagnosis && id === storedDiagnosis.id) {
      return storedDiagnosis
    }
    const diagnosis = await getDiagnosis(id)
    commit('setDiagnosis', diagnosis)
    return diagnosis
  },
  async fetchDiagnosisExpanded ({ commit, getters }, id) {
    const storedDiagnosis = getters.getDiagnosis
    if (storedDiagnosis && id === storedDiagnosis.id && storedDiagnosis.expanded) {
      return storedDiagnosis
    }
    const diagnosis = await getDiagnosisExpanded(id)
    diagnosis.expanded = true
    commit('setDiagnosis', diagnosis)
    return diagnosis
  },
  fetchReportData ({ commit, getters }, data) {
    const storedReport= getters.getReportData
    if (storedReport && storedReport.diagnosis === data.diagnosisId) {
      return storedReport
    }
    return Promise.all(
      [
        _fetchFunctionsGraph(data.diagnosisId, data.templateId),
        _fetchBricksGraph(data.diagnosisId, data.templateId),
        _fetchRadarGraph(data.diagnosisId, data.templateId),
        _fetchPrioritiesGraph(data.diagnosisId, data.templateId),
        _fetchNoAppliedPrioritiesAndFunctionsTable(data.diagnosisId, data.templateId),
      ])
      .then(resultData => {
        const report = {
          diagnosis: data.diagnosisId,
          functionsGraph: resultData[0],
          bricksGraph: resultData[1],
          radarGraph: resultData[2],
          prioritiesGraph: resultData[3],
          noAppliedPrioritiesAndFunctionsTable: resultData[4],
        }
        commit('setReportData', report)
      })
  },
  async saveDiagnosis ({ commit }, data) {
    const diagnosis = await saveDiagnosis(data)
    commit('setDiagnosis', diagnosis)
    return diagnosis
  },
  async updateDiagnosis ({ commit }, payload) {
    const id = payload.id
    delete payload.id
    const diagnosis = await patchDiagnosis(id, payload)
    commit('setDiagnosis', diagnosis)
    return diagnosis
  },
  async fetchActiveDiagnosisTemplate ({ commit }) {
    const activeDiagnosisTemplateData = await getActiveDiagnosisTemplate()
    const activeDiagnosisTemplate = activeDiagnosisTemplateData.pop()
    const requirements = await _fetchTemplateRequirements(activeDiagnosisTemplate.id)
    commit('setActiveTemplateRequirements', requirements)
    const populatedTemplate = await _fetchTemplateFunctions(activeDiagnosisTemplate)
    commit('setActiveDiagnosisTemplate', populatedTemplate)
  },
  async fetchDiagnosisTemplateById ({ commit, getters }, templateId) {
    const activeTemplate = getters.getActiveDiagnosisTemplate
    if (activeTemplate && activeTemplate.id == templateId) {
      commit('setDiagnosisTemplate', activeTemplate)
      if(!getters.isLoadingActiveRequirements) {
        commit('setTemplateRequirements', getters.getActiveTemplateRequirements)
      } else {
        const requirements = await _fetchTemplateRequirements(templateId)
        commit('setTemplateRequirements', requirements)
      }
    } else {
      const diagnosisTemplate = await getDiagnosisTemplate(templateId)
      const populatedTemplate = await _fetchTemplateFunctions(diagnosisTemplate)
      commit('setDiagnosisTemplate', populatedTemplate)

      const requirements = await _fetchTemplateRequirements(templateId)
      commit('setTemplateRequirements', requirements)
    }
  },
  async fetchSelfAssessmentMajorDiscrepancies ({ commit }, diagnosisId) {
    const MAJOR_DISCREPANCIES_SCORE = 0
    const selfAssessmentMajorDiscrepancies = await getSelfAssessmentsInDiagnosis(diagnosisId, MAJOR_DISCREPANCIES_SCORE)
    commit('setSelfAssessmentMajorDiscrepancies', selfAssessmentMajorDiscrepancies.length)
  },
  fetchConsolidateReportByPlantId (_, PlantID) {
    return getConsolidatedReport(PlantID)
  },
  async updateFundamentalInSelfAssessment ({ commit } , payload) {
    const fundamental = await patchFundamentalInSelfAssessment(payload.id, payload.score)
    commit('setFundamentalInSelfAssessment', fundamental)
    return fundamental
  },
  fetchConsolidatedReport (_, payload) {
    return getConsolidatedReportFactory(payload)
  },
  saveConsolidatedReport (_, payload) {
    return saveConsolidatedReportFactory(payload)
  },
  async fetchConsolidatedReportDetail ({ commit }, payload) {
    const report = await getConsolidatedReportDetail(payload.id)
    commit('setConsolidatedReportDetail', report)
    return report
  },
  updateConsolidatedReportJob (_, payload) {
    return patchConsolidatedReportJob(payload.id, payload)
  },
  fetchFundamentalsTargetsFactory (_, payload) {
    return getFundamentalsTargetsFactory(payload)
  },
  saveFundamentalsTargetsFactory (_, payload) {
    return saveFundamentalsTargetsFactory(payload)
  },
  fetchFundamentalsTargetsByMonth (_, payload) {
    return getFundamentalTargetByMonth(payload)
  },
  updateFundamentalTarget (_, payload) {
    return patchFundamentalTarget(payload.id, payload)
  },
  fetchFunctionsStats (_, payload) {
    return getFunctionsStats(payload)
  },
  fetchTargetCurrentChartStats (_, payload) {
    return getCurrentTargetStats(payload)
  },
  fetchNumberOfZerosChartStats (_, payload) {
    return getNumberOfZerosStats(payload)
  },
  createConsolidatedReport (_, payload) {
    return createConsolidatedReport(payload)
  },
  async updateFundamentalInConsolidatedReport ({ commit }, payload) {
    const report = await patchFundamentalInConsolidatedReport(payload.id, payload)
    commit('setFundamentalsInConsolidatedReport', report)
  },
  removeConsolidatedReport (_, payload) {
    return deleteConsolidatedReport(payload)
  },
}

async function _fetchTemplateFunctions (diagnosisTemplate) {
  const diagnosisFunctions = await getDiagnosisFunctions(diagnosisTemplate.id)
  diagnosisTemplate.diagnosisFunctions = _sortBy(diagnosisFunctions, 'order')
  return diagnosisTemplate
}

async function _fetchTemplateRequirements (templateId) {
  return getDiagnosisRequirements(templateId)
}

async function _fetchBricksGraph (diagnosisId, templateID) {
  return getDiagnosisBricksGraph(diagnosisId, templateID)
}

async function _fetchFunctionsGraph (diagnosisId, templateID) {
  return getDiagnosisFunctionsGraph(diagnosisId, templateID)
}

async function _fetchRadarGraph (diagnosisId, templateID) {
  return getDiagnosisRadarGraph(diagnosisId, templateID)
}

async function _fetchPrioritiesGraph (diagnosisId, templateID) {
  return getDiagnosisPrioritiesGraph(diagnosisId, templateID)
}

async function _fetchNoAppliedPrioritiesAndFunctionsTable (diagnosisId, templateID) {
  return getDiagnosisNoAppliedPrioritiesAndFunctionsTable(diagnosisId, templateID)
}

function _filterRequirementAssessmentsWithEvidences (assessments, fundamentalInDiagnosis, getters)  {
  const assessmentsWithEvidences = []
  assessments.forEach(requirementAssessment => {
    if(requirementAssessment.evidences.length > 0) {
      requirementAssessment.fundamentalName = getters.getFundamentalById(fundamentalInDiagnosis.fundamental).name
      assessmentsWithEvidences.push(requirementAssessment)
    }
  })
  return assessmentsWithEvidences
}

export default {
  actions,
  getters: gettersObj,
  mutations,
  state: stateObj,
}
